import { type ReactNode } from 'react';

import {
    type SpendBucketSize,
    type SpendSeries,
} from '@apps/web/components/admin/llm_spend/constants';
import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';

export const CHART_HEIGHT = 260;

// Plain ISO date (YYYY-MM-DD) to "Aug 3". Parse the parts directly so the
// label is the UTC day the rollup keyed on, with no timezone math.
export function formatBucketTick(bucketStart: string): string {
    const [, month, day] = bucketStart.split('-');
    const monthLabel = new Date(Date.UTC(2000, Number(month) - 1, 1))
        .toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
        .replace('.', '');
    return `${monthLabel} ${Number(day)}`;
}

export function formatBucketTitle(
    bucketStart: string,
    bucket: SpendBucketSize,
): string {
    const tick = formatBucketTick(bucketStart);
    return bucket === 'week' ? `Week of ${tick}` : tick;
}

// A legend is always present for two or more series so identity never rests
// on color alone; a single series is named by the card title instead.
export function ChartLegend({
    series,
}: {
    readonly series: ReadonlyArray<SpendSeries>;
}) {
    if (series.length < 2) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {series.map((row) => (
                <span key={row.key} className="flex items-center gap-1.5">
                    <span
                        aria-hidden="true"
                        className="inline-block size-2.5 rounded-[3px]"
                        style={{ backgroundColor: row.color }}
                    />
                    <span className="text-muted-foreground">{row.label}</span>
                </span>
            ))}
        </div>
    );
}

export type TooltipRow = {
    readonly key: string;
    readonly label: string;
    readonly color: string;
    readonly value: string;
};

export function ChartTooltipFrame({
    title,
    rows,
    footer,
}: {
    readonly title: string;
    readonly rows: ReadonlyArray<TooltipRow>;
    readonly footer?: ReactNode;
}) {
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{title}</div>
            {rows.map((row) => (
                <div
                    key={row.key}
                    className="flex items-center justify-between gap-4"
                >
                    <span className="flex items-center gap-1.5">
                        <span
                            aria-hidden="true"
                            className="inline-block size-2.5 rounded-[3px]"
                            style={{ backgroundColor: row.color }}
                        />
                        <span className="text-muted-foreground">
                            {row.label}
                        </span>
                    </span>
                    <span className="tabular-nums">{row.value}</span>
                </div>
            ))}
            {footer !== undefined ? (
                <div className="mt-1 flex items-center justify-between gap-4 border-t pt-1 font-medium">
                    {footer}
                </div>
            ) : null}
        </div>
    );
}

export function SpendChartCard({
    title,
    description,
    action,
    className,
    children,
}: {
    readonly title: string;
    readonly description?: ReactNode;
    // Top-right control slot, e.g. the dollars/share toggle.
    readonly action?: ReactNode;
    readonly className?: string;
    readonly children: ReactNode;
}) {
    return (
        <Card className={className}>
            <CardHeader>
                <CardTitle className="text-sm">{title}</CardTitle>
                {description !== undefined ? (
                    <CardDescription className="text-xs">
                        {description}
                    </CardDescription>
                ) : null}
                {action !== undefined ? (
                    <CardAction>{action}</CardAction>
                ) : null}
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                {children}
            </CardContent>
        </Card>
    );
}

export function ChartEmpty() {
    return (
        <div
            className="text-muted-foreground flex items-center justify-center rounded-md border border-dashed text-sm"
            style={{ height: CHART_HEIGHT }}
        >
            No data in this range.
        </div>
    );
}
